<!-- ResetPassword.vue -->
<template>
  <auth-form
    :val-error="valError"
    :error-message="errorMessage"
    :show-logging-in="showLoggingIn"
    :logging-in="loggingIn"
    action-text=""
    link-text=""
    link-label=""
    link-to=""
    :show-social-logins="false"
    :show-button="!successMessage"
    @submit="handleSubmit"
    @button-click="onButtonClick"
  >
    <template #title>
      <h2>Reset password</h2>
      <p v-if="!codeSent">We will send an one time code to validate your email</p>
      <p v-if="codeSent && showCodeSentMessage" class="success-text">Code successfully sent to registered email</p>
    </template>
    <template #form-fields>
      <template v-if="successMessage">
        <div class="success-container">
          <p class="success-text">Reset password successful, we will redirect you to login.</p>
        </div>
      </template>
      <template v-else-if="!codeSent">
        <label for="email">Email *</label>
        <input
          type="email"
          id="email"
          v-model="email"
          placeholder="Enter your email"
          :class="{ 'is-invalid': submitted && !email }"
        />
        <div v-show="submitted && !email" class="invalid-feedback">
          Email is required
        </div>
      </template>
      <template v-else>
        <label for="code">Code *</label>
        <input
          type="text"
          id="code"
          v-model="code"
          placeholder="Enter the code sent to your email"
          :class="{ 'is-invalid': submitted && !code }"
        />
        <div v-show="submitted && !code" class="invalid-feedback">
          Code is required
        </div>
        <p class="password-requirements">
          Please create a secure password that meets the following requirements:
        </p>
        <ul class="requirements-list">
          <li :class="{ 'met': passwordLengthMet }">
            {{ passwordLengthMet ? '✓' : '✗' }} Be at least 8 characters long
          </li>
          <li :class="{ 'met': uppercaseMet }">
            {{ uppercaseMet ? '✓' : '✗' }} Contain at least one uppercase letter (A-Z)
          </li>
          <li :class="{ 'met': lowercaseMet }">
            {{ lowercaseMet ? '✓' : '✗' }} Contain at least one lowercase letter (a-z)
          </li>
          <li :class="{ 'met': numberMet }">
            {{ numberMet ? '✓' : '✗' }} Contain at least one number (0-9)
          </li>
          <li :class="{ 'met': specialCharMet }">
            {{ specialCharMet ? '✓' : '✗' }} Contain at least one special character (e.g., !@#$%^&*())
          </li>
        </ul>
        <label for="password">New Password *</label>
        <input
          type="password"
          id="password"
          v-model="password"
          placeholder="Enter the new password"
          :class="{ 'is-invalid': submitted && !password }"
          @input="validatePassword"
        />
        <div v-show="submitted && !password" class="invalid-feedback">
          New password is required
        </div>
        <label for="passwordRepeat">Repeat new Password *</label>
        <input
          type="password"
          id="passwordRepeat"
          v-model="passwordRepeat"
          placeholder="Repeat your new password"
          :class="{ 'is-invalid': submitted && ( !passwordRepeat || password !== passwordRepeat ) }"
        />
        <div v-show="submitted && !passwordRepeat" class="invalid-feedback">
          Password confirmation is required
        </div>
        <div v-show="submitted && passwordRepeat && password !== passwordRepeat" class="invalid-feedback">
          Passwords do not match
        </div>
      </template>
    </template>
    <template v-if="!successMessage" #button-text>
      {{ codeSent ? "Validate Code" : "Send Code" }}
    </template>
  </auth-form>
</template>

<script>
import AuthForm from "./AuthForm.vue";
import { mapState } from "vuex";
import { apiService } from "../../_services";

export default {
  components: { AuthForm },
  data() {
    return {
      email: "",
      code: "",
      password: "",
      passwordRepeat: "",
      submitted: false,
      valError: false,
      errorMessage: "",
      showLoggingIn: false,
      codeSent: false,
      successMessage: false, // Changed back to boolean
      showCodeSentMessage: false, // New property to control visibility of the "Code successfully sent" message
      // Password validation states
      passwordLengthMet: false,
      uppercaseMet: false,
      lowercaseMet: false,
      numberMet: false,
      specialCharMet: false
    };
  },
  computed: {
    ...mapState("authentication", ["loggingIn"]),
  },
  created() {
    document.title = "Quanspire | Reset Password";
    this.$store.dispatch("authentication/logout");
  },
  methods: {
    onButtonClick() {
      if (
        (!this.codeSent && this.email) ||
        (this.codeSent && this.code && this.password && this.passwordRepeat)
      ) {
        this.showLoggingIn = true;
      }
    },
    validatePassword() {
      this.passwordLengthMet = this.password.length >= 8;
      this.uppercaseMet = /[A-Z]/.test(this.password);
      this.lowercaseMet = /[a-z]/.test(this.password);
      this.numberMet = /[0-9]/.test(this.password);
      this.specialCharMet = /[!@#$%^&*()]/.test(this.password);
    },
    async handleSubmit() {
      this.submitted = true;

      if (!this.codeSent) {
        if (!this.email) {
          this.valError = true;
          this.errorMessage = "Email is required";
          this.showLoggingIn = false;
          return;
        }

        this.valError = false;
        this.errorMessage = "";
        try {
          const data = await apiService.resetPassword(this.email);
          let message = "";
          Object.keys(data).forEach((key) => {
            message = data[key];
          });

          this.errorMessage = message;

          if (message !== "Password reset code sent successfully") {
            this.valError = true;
            this.showLoggingIn = false;
          } else {
            this.codeSent = true;
            this.showCodeSentMessage = true; // Show the "Code successfully sent" message
            this.showLoggingIn = false;
            this.submitted = false;

            // Hide the "Code successfully sent" message after 5 seconds
            setTimeout(() => {
              this.showCodeSentMessage = false;
            }, 5000);
          }
        } catch (err) {
          this.valError = true;
          this.errorMessage = err.message || "An error occurred. Please try again.";
          this.showLoggingIn = false;
        }
      } else {
        if (!this.code) {
          this.valError = true;
          this.errorMessage = "Code is required";
          this.showLoggingIn = false;
          return;
        }

        if (!this.password) {
          this.valError = true;
          this.errorMessage = "New password is required";
          this.showLoggingIn = false;
          return;
        }

        if (!this.passwordRepeat) {
          this.valError = true;
          this.errorMessage = "Password confirmation is required";
          this.showLoggingIn = false;
          return;
        }

        if (this.password !== this.passwordRepeat) {
          this.valError = true;
          this.errorMessage = "Passwords do not match";
          this.showLoggingIn = false;
          return;
        }

        if (
          !(
            this.passwordLengthMet &&
            this.uppercaseMet &&
            this.lowercaseMet &&
            this.numberMet &&
            this.specialCharMet
          )
        ) {
          this.valError = true;
          this.errorMessage = "Password does not meet all requirements";
          this.showLoggingIn = false;
          return;
        }

        this.valError = false;
        this.errorMessage = "";
        try {
          const data = await apiService.validateCode(this.email, this.code, this.password);
          let message = "";
          Object.keys(data).forEach((key) => {
            message = data[key];
          });

          this.errorMessage = message;
          this.showLoggingIn = false;
          if (message === "Password has been reset successfully") {
            this.successMessage = true;
            setTimeout(() => {
              this.$router.push("/login");
            }, 5000);
          } else {
            this.valError = true;
          }
        } catch (err) {
          this.valError = true;
          this.errorMessage = err.message || "An error occurred. Please try again.";
          this.showLoggingIn = false;
        }
      }
    }
  }
};
</script>

<style scoped>
.is-invalid {
  border-color: var(--red);
}

.invalid-feedback {
  color: var(--red);
  font-size: 12px;
  margin-top: 5px;
}

.password-requirements {
  margin: 15px 0 5px;
  color: #666;
  font-size: 14px;
}

.requirements-list {
  list-style: none;
  padding: 0;
  margin: 0;
  font-size: 13px;
}

.requirements-list li {
  margin: 5px 0;
  color: #666;
  transition: color 0.2s ease;
}

.requirements-list li.met {
  color: var(--green, #28a745);
}

.success-container {
  text-align: center;
  padding: 20px;
}

/* Increase specificity to ensure the color is applied */
.success-text {
  color: var(--green, #28a745) !important; /* Use !important to override the .title p style */
  font-size: 16px;
  margin: 0;
}
</style>